import { Dispatch, MouseEvent, SetStateAction, useEffect, useRef, useState, useCallback, MutableRefObject } from "react";
import Category from "./Category";
import SelectBox from "../common/SelectBox";
import styles from "./CategoryGroup.module.css";
import Icon from "../common/Icon";
import { CategoryOptions } from "../common/Common";
import { MapToolType } from "../../screens/MapScreen";

/**
 * @interface CategoryGroupProps
 * @prop {{@link CategoryOptions}[]} categories 카테고리 항목 옵션 배열
 * @prop {number} selectedCategory 현재 선택된 카테고리 필터 (단일 지도용 - MapComplete)
 * @prop {{@link Dispatch}<{@link SetStateAction}\<number\>>} setSelectedCategory 카테고리 필터 변경 함수 (MapComplete용)
 * @prop {number[]} selectedCategoryArray 다중 지도별 선택된 카테고리 배열 (MapScreen용)
 * @prop {{@link Dispatch}<{@link SetStateAction}\<number[]\>>} setSelectedCategoryArray 카테고리 배열 변경 함수 (MapScreen용)
 * @prop {number} selectedMapIdx 현재 선택된 지도 인덱스 (MapScreen용)
 * @prop {{@link Dispatch}<{@link SetStateAction}\<number\>>} setForceUpdate 강제 업데이트 함수
 * @prop {MutableRefObject<number | null>} prevSelectedCategoryRef 이전 선택된 카테고리 참조
 * @prop {React.MutableRefObject<number>} activeIdxRef 현재 활성화된 지도 툴 인덱스 참조
 * @prop {(idx: number) => void} setActiveIdx 지도 툴 인덱스 변경 함수
 * @prop {boolean} isOverlapAnalysisActive 오버랩 분석 활성화 여부
 */
interface CategoryGroupProps {
    categories: CategoryOptions[];
    selectedCategory: number;
    setSelectedCategory?: Dispatch<SetStateAction<number>>; // MapComplete용
    setForceUpdate?: Dispatch<SetStateAction<number>>;
    prevSelectedCategoryRef?: MutableRefObject<number | null>;
    // 장소 추가 중 카테고리 선택 시 분석도구 툴 초기화를 위해 추가 | 250901 | smyun
    activeIdxRef?: React.MutableRefObject<number>; // 최신 값으로 처리하도록 Idx 대신 IdxRef 이용
    setActiveIdx?: (idx: number) => void;
    // 25.09.02 | Kim Young Jun | 오버랩/다중 지도 기능에서 사용 (MapScreen용)
    selectedCategoryArray?: number[];
    setSelectedCategoryArray?: Dispatch<SetStateAction<number[]>>;
    selectedMapIdx?: number;
    // 25.09.02 | Kim Young Jun | 오버랩 분석 동작 중인지 확인을 위한 props 추가
    isOverlapAnalysisActive?: boolean;
    // 카테고리 변경시 선택된 마커 해제를 위한 콜백
    onClearSelectedMarker?: () => void;
    categorySelectDisabled?: boolean;
}

/**
 * 지도 영역 상단 카테고리 목록 컴포넌트
 * 
 * 사용법:
 * - MapComplete: selectedCategory + setSelectedCategory 사용 (단일 지도)
 * - MapScreen: selectedCategoryArray + setSelectedCategoryArray + selectedMapIdx 사용 (다중 지도)
 * 
 * @author mjkang
 * @since 2024-08-23 10:23:40
 * @updated 2024-09-05 Kim Young Jun - 다중 지도 지원 및 최적화
 * @param props {@link CategoryGroupProps}
 * @returns CategoryGroup
 */
const CategoryGroup = ({
    categories,
    selectedCategory,
    setSelectedCategory, // MapComplete용
    setForceUpdate,
    prevSelectedCategoryRef,
    activeIdxRef,
    setActiveIdx,
    // 25.09.02 | Kim Young Jun | MapScreen용 다중 지도 카테고리 관리
    selectedCategoryArray,
    setSelectedCategoryArray,
    selectedMapIdx,
    // 25.09.02 | Kim Young Jun | 오버랩 분석 동작 중인지 확인을 위한 props 추가
    isOverlapAnalysisActive,
    // 카테고리 변경시 선택된 마커 해제를 위한 콜백
    onClearSelectedMarker,
    categorySelectDisabled,
}: CategoryGroupProps) => {
    const categoryGroupRef = useRef<HTMLDivElement>(null);
    const [showPrev, setShowPrev] = useState(false);
    const [showNext, setShowNext] = useState(false);
    const [isMobileView, setIsMobileView] = useState<boolean>(window.innerWidth <= 1024);

    useEffect(() => {
        const handleResize = () => setIsMobileView(window.innerWidth <= 1024);
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
    }, []);

    useEffect(() => {
        if (isMobileView) return;
        const wrapper = categoryGroupRef.current;
        if (!wrapper) return;

        // 초기에 한 번 상태 세팅
        changeButtons();

        // ResizeObserver 등록
        const resizeObserver = new ResizeObserver(changeButtons);
        resizeObserver.observe(wrapper);

        // scrollend 이벤트 리스너
        wrapper.addEventListener("scrollend", changeButtons);

        return () => {
            resizeObserver.disconnect();
            wrapper.removeEventListener("scrollend", changeButtons);
        };
    }, [isMobileView]);

    // 25.09.05 | Kim Young Jun | 카테고리 클릭 핸들러 - selectedCategoryArray 우선, selectedCategory fallback
    const handleCategoryClick = useCallback(
        (index: number, isActive: boolean) => (e: MouseEvent<HTMLElement>) => {
            e.stopPropagation();
            
            const next = isActive ? 0 : index;
            
            console.log(`[CATEGORY_CLICK] 카테고리 클릭: ${index}, isActive: ${isActive}, next: ${next}`);
                        
            // 배열 기반 카테고리 관리
            if (selectedCategoryArray && setSelectedCategoryArray && selectedMapIdx !== undefined) {
                const currentSelected = selectedCategoryArray[selectedMapIdx] || 0;
                
                if (currentSelected !== next) {
                    const newArray = [...selectedCategoryArray];
                    newArray[selectedMapIdx] = next;
                    console.log(`[CATEGORY_CLICK] Array 모드: [${selectedCategoryArray.join(', ')}] → [${newArray.join(', ')}]`);
                    setSelectedCategoryArray(newArray);
                }
                
                // prevRef 업데이트 및 강제 업데이트 처리
                if (prevSelectedCategoryRef) {
                    if (prevSelectedCategoryRef.current === next && setForceUpdate) {
                        setForceUpdate(Date.now());
                    }
                    prevSelectedCategoryRef.current = next;
                }
            }
            // 단일 값 기반 카테고리 관리 (MapComplete용)
            else if (setSelectedCategory) {
                console.log(`[CATEGORY_CLICK] Single 모드: ${next}`);
                
                if (prevSelectedCategoryRef?.current === next && setForceUpdate) {
                    setForceUpdate(Date.now());
                } else {
                    setSelectedCategory(next);
                }
                
                if (prevSelectedCategoryRef) {
                    prevSelectedCategoryRef.current = next;
                }
            }

            // 장소추가 모드에서 activeIdx 초기화
            if (activeIdxRef?.current === MapToolType.Add && setActiveIdx) {
                setActiveIdx(MapToolType.Init);
            }
        },
        [
            selectedCategoryArray, 
            setSelectedCategoryArray, 
            selectedMapIdx,
            setSelectedCategory,
            setForceUpdate,
            prevSelectedCategoryRef,
            activeIdxRef,
            setActiveIdx,
            isOverlapAnalysisActive,
            onClearSelectedMarker,
            categories
        ]
    );

    // 카테고리 좌우 스크롤 버튼
    const changeButtons = () => {
        if (!categoryGroupRef.current) return;

        const wrapper = categoryGroupRef.current;
        const hiddenWidth = wrapper.scrollWidth - wrapper.scrollLeft - wrapper.clientWidth;

        setShowPrev(wrapper.scrollLeft > 0);
        setShowNext(hiddenWidth > 0);
    };

    // FIXME mjkang prev, next 버튼 너비가 계산에서 빠져서 버튼 클릭 시 스크롤 위치가 살짝 어긋남
    const scrollIntoView = (e: MouseEvent<HTMLDivElement>) => {
        if (!categoryGroupRef.current) return;

        const wrapper = categoryGroupRef.current;

        // const children = wrapper.childNodes;
        // let position: ScrollLogicalPosition = "end";
        // let maxX;

        // if (e.currentTarget.id.startsWith("prev")) {
        //     maxX = wrapper.scrollLeft;
        // } else {
        //     maxX = wrapper.getBoundingClientRect().right;
        //     position = "start";
        // }

        // for (let i = 0; i <= children.length; i++) {
        //     const el = children[i] as HTMLElement;
        //     const x = el.getBoundingClientRect().right;

        //     if (x > maxX) {
        //         el.scrollIntoView({
        //             behavior: "smooth",
        //             block: "end",
        //             inline: position,
        //         });

        //         return;
        //     }
        // }

        const direction = e.currentTarget.id.startsWith("prev") ? -1 : 1;
        const scrollAmount = 200;

        wrapper.scrollBy({
            left: direction * scrollAmount,
            behavior: "smooth",
        });
    };

    if (isMobileView) {
        return (
            <div className={`${styles.wrapper}`}>
                <div className={styles.select_box}>
                    <SelectBox
                        optionList={categories.map((c) => c.label ?? "")}
                        selected={
                            selectedCategoryArray && selectedMapIdx !== undefined 
                                ? (selectedCategoryArray[selectedMapIdx] || 0)
                                : selectedCategory
                        }
                        selectOption={(value: number) => {
                           if (onClearSelectedMarker) {
                                onClearSelectedMarker();
                            }
                            
                            // 배열 기반 처리 (MapScreen용)
                            if (selectedCategoryArray && setSelectedCategoryArray && selectedMapIdx !== undefined) {
                                const newArray = [...selectedCategoryArray];
                                newArray[selectedMapIdx] = value;
                                setSelectedCategoryArray(newArray);
                                console.log(`[MOBILE_CATEGORY] Array 모드: [${newArray.join(', ')}]`);
                            } 
                            // 단일 값 처리 (MapComplete용)
                            else if (setSelectedCategory) {
                                setSelectedCategory(value);
                                console.log(`[MOBILE_CATEGORY] Single 모드: ${value}`);
                            }
                            
                            if (prevSelectedCategoryRef) prevSelectedCategoryRef.current = value;
                        }}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={`${styles.wrapper}`}>
            {showPrev && (
                <div id="prev-category" className={styles.arrow} onClick={scrollIntoView}>
                    <Icon name="arrowBack" size="small" />
                </div>
            )}
            <div className={styles.category_group} ref={categoryGroupRef}>
                {categories.map((category: CategoryOptions, index) => {
                    const currentSelected = selectedCategoryArray && selectedMapIdx !== undefined 
                        ? (selectedCategoryArray[selectedMapIdx] || 0)
                        : selectedCategory;
                    const isActive = index === currentSelected;
                    category["isActive"] = isActive;
                    category["onClick"] = handleCategoryClick(index, isActive);

                    return <Category key={`category_${index}`} {...category} label={category.label} disabled={categorySelectDisabled} />;
                })}
            </div>
            {showNext && (
                <div id="next-category" className={styles.arrow} onClick={scrollIntoView}>
                    <Icon name="arrowForward" size="small" />
                </div>
            )}
        </div>
    );
};

export default CategoryGroup;
